import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";
import API_URL from "../../api/API";
import {enterEditMode} from "./helpers";
import getQrSigningUrl from "../../components/SigningUrls";

const ActiveWaiverViewer = () => {
  const { waiverId } = useParams();
  const { business } = useBusinessAuthContext();
  const navigate = useNavigate();

  const [waiver, setWaiver] = useState(null);
  const [newVersionWaiver, setNewVersionWaiver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [versionHistory, setVersionHistory] = useState([]);

  useEffect(() => {
    if (!business) {
      navigate("/business/login");
      return;
    }

    const loadWaiver = async () => {
      try {
        const res = await fetch(
          `${API_URL.BUSINESS_WAIVER_BY_ID}/${waiverId}`,
          {
            headers: {
              Authorization: `Bearer ${business.token}`,
              "x-session-id": business.sessionId,
              "x-frontend-url": window.location.pathname,
            },
          }
        );

        const data = await res.json();
 
        if (data.success) {

          setWaiver(data.waiver);

          /* //fetch historical versions
          const versionRes = await fetch(
            `${API_URL.BUSINESS_WAIVER_VERSION_HISTORY}/${waiverId}`,
            {
              headers: {
                Authorization: `Bearer ${business.token}`,
                "x-session-id": business.sessionId,
                "x-frontend-url": window.location.pathname,
              },
            }
          );

          const versionData = await versionRes.json();

          if (versionData.success) {
            setVersionHistory(versionData.versions || []);
          } */

          //fetch new verion info
          const listRes = await fetch(API_URL.BUSINESS_WAIVERS_LIST, {
            headers: {
              Authorization: `Bearer ${business.token}`,
              "x-session-id": business.sessionId,
              "x-frontend-url": window.location.pathname,
            },
          });
          const listData = await listRes.json();
          if (listData.success) {
            const existingNewVersion = listData.waivers.find(
              (w) =>
                w.waiverStatus === "new_version" &&
                String(w.versionOfWaiverId) === String(waiverId)
            );
            setNewVersionWaiver(existingNewVersion || null);
          }
        }
      } catch (err) {
        console.error("Failed loading active waiver", err);
      } finally {
        setLoading(false);
      }
    };

    loadWaiver();
  }, [business, waiverId, navigate]);

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getVersionLabel = () => {
    if (waiver?.versionLabel) {
      return waiver.versionLabel;
    }

    if (waiver?.version) {
      return `v${waiver.version}`;
    }

    return "-";
  };

  const handleBack = () => {
    navigate("/business/waivers");
  };

  const handlePreview = () => {
    navigate(`/business/waiverbuilder/smart/${waiverId}/step-7`);
  };

  const handleEdit = async () => {
  // This specific waiver already has a new version
  if (newVersionWaiver) {
    if (!(await enterEditMode(newVersionWaiver, business))) return;

    navigate(`/business/waivereditor/${newVersionWaiver._id}`);
    return;
  }
  const handleViewVersion = (versionWaiverId) => {
    navigate(`/business/waiverviewer/${versionWaiverId}`);
  };

  const handleViewSignatures = (versionId) => {
    navigate(
      `/business/waivers/signatures/${versionId}`
    );
  };

  // This specific waiver does not have a new version yet
  try {
    const res = await fetch(
      `${API_URL.BUSINESS_CREATE_NEW_VERSION}/${waiverId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${business.token}`,
          "x-session-id": business.sessionId,
          "x-frontend-url": window.location.pathname,
        },
      }
    );

    const data = await res.json();

    if (data.success) {
      navigate(`/business/waivereditor/${data.newVersionWaiverId}`);
    } else {
      alert(data.message || "Cannot create new version");
    }
  } catch (err) {
    console.error(err);
    alert("Server error");
  }
};

  const handleViewAllVersions = () => {
    navigate(`/business/waiverviewer/${waiverId}/version-history`);
  };


  const handleViewSignatures = () => {
    navigate(`/business/waivers/signatures/${waiverId}`);
  };

  const handleViewCurrentVersion = () => {
    navigate(`/business/waiverviewer/${waiverId}/content`);
  };
  const handleViewVersion = (versionWaiverId) => {
    navigate(
      `/business/waiverviewer/${versionWaiverId}`
    );
    /* navigate(
      `/business/waiverviewer/${versionWaiverId}/version-history`
    ); */
  };

  const handleDisable = async () => {
    /*
      Keep your existing disable-waiver logic here.

      For now this only provides the UI action.
    */
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-gray-500">
          Loading waiver...
        </div>
      </div>
    );
  }

  if (!waiver) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-gray-500">
          Waiver not found.
        </div>
      </div>
    );
  }

  /*
    These fallbacks allow the UI to work even if your backend
    currently uses slightly different field names.
  */

  const totalSignatures =
    waiver.totalSignatures ??
    waiver.signatureCount ??
    waiver.signaturesCount ??
    0;

  const lastSigned =
    waiver.lastSignedAt ??
    waiver.lastSigned ??
    waiver.latestSignatureAt ??
    null;

  const publishedAt =
    waiver.publishedAt ??
    waiver.updatedAt ??
    waiver.createdAt ??
    null;

  const isActive =
    waiver.waiverStatus === "published";

  const minorsAllowed = waiver.minorWaiverAllowed ?? false;
  
  const normalizePolicy = (value) => {
    if (!value) return null;

    const v = String(value).trim().toLowerCase();

    if (v === "standard_resign") return "Standard";
    if (v === "quick_resign") return "Quick";

    return null; // anything else is invalid
  };
  const returningSigners = normalizePolicy(
    waiver.returningSignerPolicy
  );

  const timezone =
    waiver.timezone ??
    waiver.businessTimezone ??
    "America/Los_Angeles (PST)";

  const retention =
    waiver.recordRetention ??
    "Signed records are retained permanently for audit and legal compliance.";

  const locationUniqueSlug =
    typeof waiver.locations?.[0] === "object"
      ? waiver.locations[0]?.uniqueSlug
      : null;

  // Normal URL for sharing with customers
  const signingUrl =
    `${window.location.origin}/start-waiver/` +
    `${locationUniqueSlug}/` +
    `${waiver.businessTypeId?.uniqueSlug}/` +
    `${waiver.service?.uniqueSlug}`;

  // QR-specific URL
  const qrSigningUrl = getQrSigningUrl(signingUrl);

  const downloadQR = () => {
    const svg = document.getElementById("active-waiver-qr");
    if (!svg) return;

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);

    const blob = new Blob(
      [source],
      { type: "image/svg+xml;charset=utf-8" }
    );

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `qr-${waiverId}.svg`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const printQR = () => {
    const svg = document.getElementById("active-waiver-qr");
    if (!svg) return;

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);

    const w = window.open("");
    w.document.write(source);
    w.document.close();
    w.print();
  };

  const copyLink = async (url) => {
    await navigator.clipboard.writeText(url);
    alert("Link copied to clipboard");
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-5">

        {/* Back */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-5"
        >
          <span className="text-lg">‹</span>
          Back to Waivers
        </button>

        {/* ========================================================= */}
        {/* HEADER                                                     */}
        {/* ========================================================= */}

        <div className="border border-gray-200 rounded-2xl px-6 py-5 mb-3">

          <div className="flex items-center justify-between gap-6">

            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                {waiver.name || waiver.title || "Waiver"}
              </h1>

              <div className="flex items-center gap-3 mt-2">

                {isActive ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                    Inactive
                  </span>
                )}

                {isActive && (
                  <button
                    onClick={handleDisable}
                    className="text-xs text-gray-500 underline hover:text-gray-900"
                  >
                    Disable
                  </button>
                )}

              </div>
            </div>

            <div className="flex items-center gap-3">

              <button
                onClick={handlePreview}
                className="h-12 px-6 rounded-full border border-gray-900 text-sm font-medium flex items-center gap-2 hover:bg-gray-50"
              >
                <span className="text-base">◉</span>
                Preview Live Waiver
              </button>

              <button
                onClick={handleEdit}
                className="h-12 px-6 rounded-full bg-yellow-400 hover:bg-yellow-500 text-sm font-medium flex items-center gap-2"
              >
                <span className="text-base">✎</span>
                Edit Waiver
              </button>

            </div>

          </div>
        </div>

        {/* ========================================================= */}
        {/* SIGNATURE SUMMARY                                         */}
        {/* ========================================================= */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">

          {/* Total Signatures */}
          <div className="border border-gray-200 rounded-2xl px-6 py-5 flex items-center gap-4">

            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center text-green-700">
              ✍
            </div>

            <div>
              <div className="text-xs text-gray-500 mb-1">
                Total Signatures
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-900">
                  {totalSignatures}
                </span>

                <button
                  onClick={handleViewSignatures}
                  className="text-xs underline text-gray-700 hover:text-black"
                >
                  View All Signatures
                </button>
              </div>
            </div>

          </div>

          {/* Last Signed */}
          <div className="border border-gray-200 rounded-2xl px-6 py-5 flex items-center gap-4">

            <div className="w-10 h-10 rounded-xl bg-yellow-50 flex items-center justify-center text-yellow-700">
              ▣
            </div>

            <div>
              <div className="text-xs text-gray-500 mb-1">
                Last Signed
              </div>

              <div className="text-sm text-gray-900">
                {formatDate(lastSigned)}
              </div>
            </div>

          </div>

        </div>

        {/* ========================================================= */}
        {/* VERSION HISTORY                                           */}
        {/* ========================================================= */}

        <div className="border border-gray-200 rounded-2xl px-6 py-5 mb-3">

          <div className="flex items-center justify-between mb-5">

            <h2 className="text-lg font-semibold text-gray-900">
              Version History
            </h2>

            <button
              onClick={handleViewAllVersions}
              className="text-xs text-gray-500 underline hover:text-gray-900"
            >
              View all versions
            </button>

          </div>

          <div className="border border-gray-200 rounded-xl overflow-hidden">

            <div className="grid grid-cols-[1fr_1fr_1fr_1.2fr] bg-gray-50 px-4 py-3 text-xs font-medium text-gray-700">

              <div>Version</div>
              <div>Status</div>
              <div>Signatures</div>
              <div>Published</div>
              <div>Actions</div>
            </div>

            <div className="grid grid-cols-[1fr_1fr_1fr_1.2fr] px-4 py-4 text-sm">

              <div>
                {getVersionLabel()}
              </div>

              <div>
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                  Active
                </span>
              </div>

              <div>
                {totalSignatures}
              </div>

              <div >
                {formatDate(publishedAt)}
              </div>

              <div className="text-gray-500">
                <div className="flex gap-4">
                  <button
                    onClick={() =>
                      handleViewVersion(waiverId)
                    }
                    className="text-sm underline"
                  >
                    View Version
                  </button>

                  <button
                    onClick={() =>
                      handleViewSignatures(waiverId)
                    }
                    className="text-sm underline"
                  >
                    View Signatures
                  </button>
                </div>
              </div>

            </div>

          </div>

        </div>

        {/* ========================================================= */}
        {/* ELIGIBILITY RULES                                         */}
        {/* ========================================================= */}

        <div className="border border-gray-200 rounded-2xl px-6 py-5 mb-3">

          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            Eligibility Rules
          </h2>

          <div className="space-y-5">

            <div className="flex items-center justify-between text-sm">

              <span className="text-gray-500">
                Minors
              </span>

              <span className="text-gray-800">
                {minorsAllowed ? "Allowed" : "Not Allowed"}
              </span>

            </div>

            <div className="border-t border-gray-100" />

            <div className="flex items-center justify-between text-sm">

              <span className="text-gray-500">
                Returning signers
              </span>

              <span className="text-gray-800">
                {returningSigners}
              </span>

            </div>

          </div>

        </div>

        {/* ========================================================= */}
        {/* SCAN + SHARE                                              */}
        {/* ========================================================= */}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">

          {/* Scan in Person */}
          <div className="border border-gray-200 rounded-2xl px-6 py-5">

            <div className="flex items-start gap-4">

              <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-700">
                ◉
              </div>

              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Scan in person
                </h2>

                <p className="text-xs text-gray-500 mt-1">
                  Customers scan to sign on their own device.
                </p>
              </div>

            </div>

            <div className="mt-5 flex flex-col items-start gap-3">

              <div className="w-32 h-32 border-2 border-gray-900 rounded-2xl flex items-center justify-center bg-white">
                <QRCodeSVG
                  id="active-waiver-qr"
                  value={qrSigningUrl}
                  size={112}
                  includeMargin
                />
              </div>

              <div className="flex gap-3">

                <button
                  onClick={downloadQR}
                  className="px-5 py-2.5 rounded-full border border-gray-900 text-sm hover:bg-gray-50"
                >
                  ↓ Download QR
                </button>

                <button
                  onClick={printQR}
                  className="px-5 py-2.5 rounded-full border border-gray-900 text-sm hover:bg-gray-50"
                >
                  ▣ Print QR
                </button>

              </div>

            </div>

          </div>

          {/* Share signing link */}
          <div className="border border-gray-200 rounded-2xl px-6 py-5">

            <div className="flex items-start gap-4">

              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-700">
                ↗
              </div>

              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Share signing link
                </h2>

                <p className="text-xs text-gray-500 mt-1">
                  Send via text, email or website.
                </p>
              </div>

            </div>

            <div className="mt-5">

              <div className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3 flex items-center gap-3">

                <div className="flex-1 text-xs text-gray-600 break-all">
                  {signingUrl}
                </div>

                <button
                  /*onClick={() => {
                    navigator.clipboard.writeText(signingUrl);
                  }}*/
                  onClick={() => copyLink(signingUrl)}
                  className="text-gray-500 hover:text-gray-900"
                  title="Copy signing link"
                >
                  ⧉
                </button>

              </div>

              <div className="mt-4 border border-gray-200 rounded-xl px-4 py-3 flex items-start gap-3">

                <div className="text-gray-500">
                  ⓘ
                </div>

                <div>

                  <div className="text-sm font-medium text-gray-800">
                    Always uses the currently active version.
                  </div>

                  <div className="text-xs text-gray-500 mt-1">
                    Past signatures are never affected.
                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

        {/* ========================================================= */}
        {/* FOOTER INFORMATION                                        */}
        {/* ========================================================= */}

        <div className="border border-gray-200 rounded-2xl px-6 py-5 bg-gray-50">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            <div>

              <div className="text-xs text-gray-500 mb-1">
                Waiver ID (UUID)
              </div>

              <div className="text-xs text-gray-700 break-all">
                {waiver.waiverId ||
                  waiver.uuid ||
                  waiver._id ||
                  "-"}
              </div>

            </div>

            <div>

              <div className="text-xs text-gray-500 mb-1">
                Timezone
              </div>

              <div className="text-xs text-gray-700">
                {timezone}
              </div>

            </div>

            <div>

              <div className="text-xs text-gray-500 mb-1">
                Record Retention
              </div>

              <div className="text-xs text-gray-700">
                {retention}
              </div>

            </div>

          </div>

        </div>

      </div>
    </div>
  );
};

export default ActiveWaiverViewer;